import * as THREE from 'three';
import { CONFIG } from '../config';

export type SceneContext = {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  orbPos: THREE.Vector3;
};

export function createSceneContext(canvas: HTMLCanvasElement): SceneContext {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x1a2a1a);
  scene.fog = new THREE.Fog(CONFIG.scene.fogColor, CONFIG.scene.fogNear, CONFIG.scene.fogFar);

  const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 200);
  camera.position.set(0, 2.4, 8.5);
  camera.lookAt(0, 1.2, 0);

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.1;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const sun = new THREE.DirectionalLight(0xfdb, 1.6);
  sun.position.set(6, 12, 4);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.left = -20;
  sun.shadow.camera.right = 20;
  sun.shadow.camera.top = 20;
  sun.shadow.camera.bottom = -20;
  sun.shadow.camera.near = 0.5;
  sun.shadow.camera.far = 40;
  sun.shadow.bias = -0.0005;
  scene.add(sun);

  const hemi = new THREE.HemisphereLight(0xaaccff, 0x3d5a30, 0.4);
  scene.add(hemi);

  const orbLight = new THREE.PointLight(0xffd0a0, 2.5, 12, 1.5);
  orbLight.position.set(...CONFIG.orb.position);
  scene.add(orbLight);

  const groundGeo = new THREE.CircleGeometry(CONFIG.scene.groundSize, 64);
  const groundMat = new THREE.MeshStandardMaterial({
    color: CONFIG.colors.dirtGround,
    roughness: 0.95,
    metalness: 0,
  });
  const ground = new THREE.Mesh(groundGeo, groundMat);
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  scene.add(ground);

  for (let i = 0; i < 6; i++) {
    const r = 8 + i * 2.5;
    const ringGeo = new THREE.RingGeometry(r, r + 0.3, 64);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0x1f2e1a,
      transparent: true,
      opacity: 0.35 - i * 0.04,
      side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.01;
    scene.add(ring);
  }

  return {
    scene,
    camera,
    renderer,
    orbPos: new THREE.Vector3(...CONFIG.orb.position),
  };
}

export function resizeRenderer(ctx: SceneContext): void {
  const w = window.innerWidth;
  const h = window.innerHeight;
  ctx.renderer.setSize(w, h, false);
  ctx.camera.aspect = w / h;
  ctx.camera.updateProjectionMatrix();
}
